import { Dataowner, EmployeeDetail, OrgUnitRole, UserData } from '@dap/memberadmin/types';
import { RoleType } from '@dap/common/types';
import { DataownerBasic, LocationOverview, InformationModelType } from '@dap/memberadmin/types';

export function hasEditUserPermission(
  me: UserData,
  employee: EmployeeDetail,
  orgUnitKey: string
): boolean {
  return (
    isSuperuser(me) ||
    isOrgUnitAdmin(me, orgUnitKey) ||
    me?.userId === employee.userId ||
    isAdmin(me, orgUnitKey)
  );
}

function isOrgUnitAdmin(userData: UserData, orgUnitKey: string) {
  return userData.roles.some((role) => role.role.isAdmin && role.orgUnitKey === orgUnitKey);
}

export function isSuperuser(userData: UserData): boolean {
  return userData.metadata.superuser;
}

/**
 *
 * @param userData The current userData object
 * @param orgUnitKey The key of the orgUnit
 * @returns true if the user has an admin role in the given OrgUnit
 */
export function isAdmin(userData: UserData, orgUnitKey: string): boolean {
  return isSuperuser(userData) || isOrgUnitAdmin(userData, orgUnitKey);
}

export function isBrandObserver(userData: UserData, orgUnitKey: string): boolean {
  return (
    isSuperuser(userData) ||
    userData.roles.some(
      (role) => role.role.key === RoleType.BrandObserver && role.orgUnitKey === orgUnitKey
    )
  );
}

export function isMasterdataAdmin(userData: UserData, orgUnitKey: string): boolean {
  return (
    isSuperuser(userData) ||
    userData.roles.some(
      (role) =>
        role.role.isAdmin &&
        role.role.key === RoleType.MasterdataAdmin &&
        role.orgUnitKey === orgUnitKey
    )
  );
}

/*
 * Iterates the roles of a user to check if the user satifies roles to get access to Medlemsadmin
 * - has the dataowner role "medlemadmin" on a dataowner that is part of the current brand
 * - has the location role "lokasjonsadministrator" on a location that is part of the current brand
 */
export function hasMemberadminAccess(
  userData: UserData,
  locations: LocationOverview[],
  dataowners: DataownerBasic[]
): boolean {
  // Iterate all roles, return an array of location keys that the user has the medlemsadmin or lokasjonsadministrator role on
  const memberAdminRoles = userData.roles
    .filter((role) => {
      if (role.type === InformationModelType.Dataowner && role.role.key === RoleType.Medlemsadmin) {
        // Check if the dataowner belongs to the current brand
        return dataowners.some((dataowner) => dataowner.key === role.orgUnitKey);
      }

      if (
        role.type === InformationModelType.Location &&
        role.role.key === RoleType.Lokasjonsadministrator
      ) {
        // Check if the location belongs to the current brand
        return locations.some((location) => location.key === role.orgUnitKey);
      }

      return false;
    })
    .map((role) => role.orgUnitKey);

  // User has access if there are any related roles
  return memberAdminRoles.length > 0;
}

// Returns true if the user has the medlemsadmin role on the given dataowner
export const isMemberadminForDataowner = (userData: UserData, dataowner: Pick<Dataowner, 'key'>) => {
  return userData.roles.some(
    (role) =>
      role.type === InformationModelType.Dataowner &&
      role.role.key === RoleType.Medlemsadmin &&
      role.orgUnitKey === dataowner.key
  );
};

export const isLocationadminForLocation = (userData: UserData, locationKey: string) => {
  return userData.roles.some(
    (role) =>
      role.type === InformationModelType.Location &&
      role.role.key === RoleType.Lokasjonsadministrator &&
      role.orgUnitKey === locationKey
  );
};

/**
 * Locations a user may manage employees on for a member.
 * Member admins (and superusers) get every location; location admins only get
 * the locations they hold lokasjonsadministrator on.
 */
export function getManagedLocations<T extends { key: string }>(
  userData: UserData,
  member: Pick<Dataowner, 'key'> & { locations?: T[] }
): T[] {
  const locations = member.locations ?? [];

  if (isSuperuser(userData) || isMemberadminForDataowner(userData, member)) {
    return locations;
  }

  return locations.filter((location) => isLocationadminForLocation(userData, location.key));
}

/**
 * Which of a member's properties a user may see.
 * Users that are only location admins see just the properties of the locations they manage,
 * and none of the member-level properties. Everyone else sees all locations.
 */
export function getPropertyVisibility<T extends { key: string }>(
  userData: UserData,
  member: Pick<Dataowner, 'key'> & { locations?: T[] }
): { isLocationAdminOnly: boolean; visibleLocations: T[] } {
  const isMemberAdmin = isSuperuser(userData) || isMemberadminForDataowner(userData, member);
  const managedLocations = getManagedLocations(userData, member);
  const isLocationAdminOnly = !isMemberAdmin && managedLocations.length > 0;

  return {
    isLocationAdminOnly,
    visibleLocations: isLocationAdminOnly ? managedLocations : member.locations ?? [],
  };
}

/**
 * Whether a user may assign admin location roles (e.g. lokasjonsadministrator) on a member.
 * Users that are only location admins for the member may not, to prevent privilege escalation.
 */
export function canAssignAdminLocationRoles(
  userData: UserData,
  member: Pick<Dataowner, 'key'> & { locations?: { key: string }[] }
): boolean {
  if (isMemberadminForDataowner(userData, member)) {
    return true;
  }

  const isLocationAdmin = (member.locations ?? []).some((location) =>
    isLocationadminForLocation(userData, location.key)
  );

  return !isLocationAdmin;
}

/**
 * Admin location roles an employee holds that the current user may not change.
 * Only roles on editable locations are returned, as other locations are never patched.
 */
export function getLockedLocationRoles<T extends Pick<OrgUnitRole, 'role' | 'orgUnitKey' | 'type'>>(
  employeeRoles: T[] | undefined,
  canAssignAdminRoles: boolean,
  editableLocationKeys?: string[]
): T[] {
  if (canAssignAdminRoles) {
    return [];
  }

  return (employeeRoles ?? []).filter(
    (role) =>
      role.type === InformationModelType.Location &&
      role.role.isAdmin &&
      (!editableLocationKeys || editableLocationKeys.includes(role.orgUnitKey))
  );
}

export function filterEmployeesByLocationKeys<T extends { roles?: EmployeeDetail['roles'] }>(
  employees: T[],
  locationKeys: string[]
): T[] {
  if (locationKeys.length === 0) {
    return [];
  }

  const locationKeySet = new Set(locationKeys);

  return employees.filter((employee) =>
    employee.roles?.some(
      (role) =>
        role.type === InformationModelType.Location && locationKeySet.has(role.orgUnitKey)
    )
  );
}
